/**
 * UnreadBadge Component
 *
 * Displays unread message count with polling.
 * Used in navigation to show pending messages.
 */

import { useState, useEffect, useCallback } from 'react';
import { getUnreadCount } from '../../api/messages';

interface UnreadBadgeProps {
  className?: string;
}

export default function UnreadBadge({ className = '' }: UnreadBadgeProps) {
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { count } = await getUnreadCount();
      setCount(count);
      setError(false);
    } catch {
      // Silently fail - badge just won't show
      setError(true);
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchCount();

    // Poll every 60 seconds
    const interval = setInterval(fetchCount, 60000);

    return () => clearInterval(interval);
  }, [fetchCount]);

  // Don't show anything if count is 0 or there was an error
  if (count === 0 || error) {
    return null;
  }

  // Format count (show 99+ for large numbers)
  const displayCount = count > 99 ? '99+' : count.toString();

  return (
    <span
      className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 ${className}`}
    >
      {displayCount}
    </span>
  );
}
